import appConfig from "../config";

const rawConsoleUrl = appConfig.domain.subDomian;
const consoleUrl = (
  /^https?:\/\//i.test(rawConsoleUrl) ? rawConsoleUrl : `https://${rawConsoleUrl}`
).replace(/\/+$/, "");

/** Fallback versions if policy API is unavailable */
export const AI_CONSENT_POLICY_VERSIONS = {
  terms_version: "1.0",
  data_policy_version: "1.0",
  privacy_policy_version: "2024-08"
};

export const AI_CONSENT_PROVIDER = {
  provider_code: "ANTHROPIC",
  provider_display_name: "Claude (Anthropic)"
};

/** Show Anthropic in brackets, e.g. "Claude (Anthropic)". */
export function formatProviderDisplayName(value) {
  const label = String(value || AI_CONSENT_PROVIDER.provider_display_name).trim();
  if (!label) return AI_CONSENT_PROVIDER.provider_display_name;
  return label
    .replace(/\bAnthropic\b(?!\))/i, "(Anthropic)")
    .replace(/\s+by\s+(?=\(Anthropic\))/i, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export const AI_CONSENT_POLICY_LINKS = {
  termsOfUse: `${consoleUrl}/termscondition`,
  aiDataUse: `${consoleUrl}/data-policy`,
  privacyPolicy: "https://www.acumen.aero/policy"
};

export const AI_TERMS_ACCEPTANCE_REQUIRED = "AI_TERMS_ACCEPTANCE_REQUIRED";

/** Single standard modal message for missing / outdated consent. */
export const AI_CONSENT_STANDARD_MESSAGE =
  "Your AI consent is missing or outdated. Please review and accept the current terms to continue.";

/** @deprecated Use AI_CONSENT_STANDARD_MESSAGE */
export const AI_CONSENT_REQUIRED_MESSAGE = AI_CONSENT_STANDARD_MESSAGE;

export function getConsentModalMessage({ saveFailed = false } = {}) {
  if (saveFailed) {
    return "We couldn't save your acceptance. Please review and accept the current terms to continue.";
  }
  return AI_CONSENT_STANDARD_MESSAGE;
}

function unwrapApiPayload(response) {
  if (response && typeof response === "object" && "data" in response && response.data) {
    return response.data;
  }
  return response;
}

/**
 * Normalize GET /api/ai/consent/policy response.
 * Expected shape:
 * {
 *   id, terms_version, data_policy_version, privacy_policy_version,
 *   terms_url, data_policy_url, privacy_policy_url,
 *   provider_code, provider_display_name, requires_reacceptance,
 *   policy_links: { terms_of_use, ai_data_use, privacy_policy }
 * }
 */
export function normalizeConsentPolicy(response) {
  const root = unwrapApiPayload(response) || {};
  const nested =
    root.policy && typeof root.policy === "object" ? root.policy : {};
  const links =
    root.policy_links && typeof root.policy_links === "object"
      ? root.policy_links
      : {};

  const terms_version = String(
    root.terms_version ??
      nested.terms_version ??
      links.terms_of_use?.version ??
      AI_CONSENT_POLICY_VERSIONS.terms_version
  );
  const data_policy_version = String(
    root.data_policy_version ??
      nested.data_policy_version ??
      links.ai_data_use?.version ??
      AI_CONSENT_POLICY_VERSIONS.data_policy_version
  );
  const privacy_policy_version = String(
    root.privacy_policy_version ??
      nested.privacy_policy_version ??
      links.privacy_policy?.version ??
      AI_CONSENT_POLICY_VERSIONS.privacy_policy_version
  );

  const terms_url =
    root.terms_url ||
    nested.terms_url ||
    links.terms_of_use?.url ||
    AI_CONSENT_POLICY_LINKS.termsOfUse;
  const data_policy_url =
    root.data_policy_url ||
    nested.data_policy_url ||
    links.ai_data_use?.url ||
    AI_CONSENT_POLICY_LINKS.aiDataUse;
  const privacy_policy_url =
    root.privacy_policy_url ||
    nested.privacy_policy_url ||
    links.privacy_policy?.url ||
    AI_CONSENT_POLICY_LINKS.privacyPolicy;

  return {
    id: root.id ?? nested.id ?? null,
    terms_version,
    data_policy_version,
    privacy_policy_version,
    terms_url,
    data_policy_url,
    privacy_policy_url,
    provider_code:
      root.provider_code || nested.provider_code || AI_CONSENT_PROVIDER.provider_code,
    provider_display_name: formatProviderDisplayName(
      root.provider_display_name ||
        nested.provider_display_name ||
        AI_CONSENT_PROVIDER.provider_display_name
    ),
    requires_reacceptance: Boolean(
      root.requires_reacceptance ?? nested.requires_reacceptance
    ),
    published_at: root.published_at || nested.published_at || null,
    policy_links: {
      termsOfUse: {
        label: links.terms_of_use?.label || "Terms of Use",
        href: terms_url,
        version: links.terms_of_use?.version || terms_version
      },
      aiDataUse: {
        label: links.ai_data_use?.label || "AI & Data Use",
        href: data_policy_url,
        version: links.ai_data_use?.version || data_policy_version
      },
      privacyPolicy: {
        label: links.privacy_policy?.label || "Privacy Policy",
        href: privacy_policy_url,
        version: links.privacy_policy?.version || privacy_policy_version
      }
    }
  };
}

export function normalizeConsentStatus(response) {
  const root = unwrapApiPayload(response) || {};

  // Prefer nested consent/acceptance objects from GET/POST responses.
  const payload =
    (root?.consent && typeof root.consent === "object" && root.consent) ||
    (root?.acceptance && typeof root.acceptance === "object" && root.acceptance) ||
    root ||
    {};

  const status = String(payload?.status || root?.status || "").toLowerCase();
  const reacceptanceRequired = Boolean(
    payload?.requires_reacceptance ||
      payload?.reacceptance_required ||
      payload?.re_consent_required ||
      root?.requires_reacceptance ||
      status === "reacceptance_required"
  );
  const accepted = Boolean(
    payload?.accepted === true ||
      root?.acceptance?.accepted === true ||
      status === "accepted"
  );

  // Prefer explicit chat_access_allowed from API when present.
  const explicitAccess =
    typeof payload?.chat_access_allowed === "boolean"
      ? payload.chat_access_allowed
      : typeof root?.chat_access_allowed === "boolean"
        ? root.chat_access_allowed
        : null;
  const chatAccessAllowed =
    explicitAccess === null
      ? Boolean(accepted && !reacceptanceRequired)
      : explicitAccess && !reacceptanceRequired;

  return {
    ...payload,
    message: root?.message,
    acceptance: root?.acceptance,
    accepted,
    chatAccessAllowed,
    reacceptanceRequired,
    required: !chatAccessAllowed || reacceptanceRequired
  };
}

/**
 * Candidate check only — a 403 may or may not mean consent is required.
 * Always confirm with GET /api/ai/consent before opening the modal.
 */
export function isForbiddenError(error) {
  const status = Number(
    error?.status ?? error?.response?.status ?? error?.data?.status
  );
  if (status === 403) return true;
  // Fallback when status was dropped but axios message remains.
  const message = String(error?.message ?? "").toLowerCase();
  return message.includes("status code 403");
}

/** True when a 403 payload looks like consent enforcement. */
export function isConsentEnforcementHint(error) {
  if (!isForbiddenError(error)) return false;

  const code = String(
    error?.data?.code ??
      error?.data?.error_code ??
      error?.response?.data?.code ??
      error?.response?.data?.error_code ??
      (typeof error?.data?.error === "string" ? error.data.error : "") ??
      ""
  );
  const message = String(
    error?.data?.message ??
      error?.response?.data?.message ??
      error?.message ??
      ""
  ).toLowerCase();

  return (
    code === AI_TERMS_ACCEPTANCE_REQUIRED ||
    message.includes(AI_TERMS_ACCEPTANCE_REQUIRED.toLowerCase()) ||
    message.includes("terms acceptance") ||
    message.includes("consent") ||
    message.includes("re-accept") ||
    message.includes("reaccept")
  );
}

/**
 * True when the browser could not read the response (no status).
 * A CORS-blocked 403 surfaces here as "Network Error", so consent must still be verified.
 */
export function isOpaqueApiError(error) {
  const status = Number(
    error?.status ?? error?.response?.status ?? error?.data?.status
  );
  if (Number.isFinite(status) && status > 0) return false;

  const code = String(error?.code ?? "").toUpperCase();
  const message = String(error?.message ?? "").toLowerCase();
  return (
    code === "ERR_NETWORK" ||
    message.includes("network error") ||
    message.includes("failed to fetch") ||
    message.includes("cors")
  );
}

/** Errors that require a GET /api/ai/consent verification before showing the modal. */
export function isConsentCheckCandidate(error) {
  return isForbiddenError(error) || isOpaqueApiError(error);
}

/** @deprecated Prefer isConsentCheckCandidate + GET consent verification. */
export function isConsentBlockedError(error) {
  return isForbiddenError(error);
}

function pickUserName(item = {}) {
  const nestedUser = item.user && typeof item.user === "object" ? item.user : null;
  return (
    item.user_name ||
    item.full_name ||
    item.name ||
    nestedUser?.name ||
    [nestedUser?.first_name, nestedUser?.last_name].filter(Boolean).join(" ").trim() ||
    nestedUser?.email ||
    item.email ||
    `User ${item.user_id ?? item.id ?? ""}`
  );
}

function pickStatusLabel(item = {}) {
  if (item.status_label) return item.status_label;
  if (item.requires_reacceptance || item.reacceptance_required) {
    return "Re-acceptance Required";
  }
  if (item.accepted === true || String(item.status || "").toLowerCase() === "accepted") {
    return "Accepted";
  }
  if (item.accepted === false || String(item.status || "").toLowerCase() === "not_accepted") {
    return "Not Accepted";
  }
  return item.status || "Unknown";
}

export function normalizeConsentUserRecord(item = {}) {
  const nestedUser = item.user && typeof item.user === "object" ? item.user : null;
  const consent =
    (item.consent && typeof item.consent === "object" && item.consent) ||
    (item.current_consent && typeof item.current_consent === "object" && item.current_consent) ||
    item;

  const userId = item.user_id ?? nestedUser?.id ?? consent.user_id ?? item.id;
  const accepted = Boolean(
    consent.accepted === true || String(consent.status || "").toLowerCase() === "accepted"
  );

  return {
    ...item,
    ...consent,
    user_id: userId,
    user_name: pickUserName(item),
    email: item.email || nestedUser?.email || consent.email || "",
    organisation_id:
      item.organisation_id || consent.organisation_id || item.lessor_slug || "",
    accepted,
    accepted_at: consent.accepted_at || item.accepted_at || null,
    status: consent.status || item.status || (accepted ? "accepted" : "not_accepted"),
    status_label: pickStatusLabel({ ...item, ...consent, accepted }),
    provider_display_name: formatProviderDisplayName(
      consent.provider_display_name ||
        item.provider_display_name ||
        AI_CONSENT_PROVIDER.provider_display_name
    ),
    provider_code:
      consent.provider_code || item.provider_code || AI_CONSENT_PROVIDER.provider_code,
    terms_version: consent.terms_version || item.terms_version || "",
    data_policy_version: consent.data_policy_version || item.data_policy_version || "",
    privacy_policy_version:
      consent.privacy_policy_version || item.privacy_policy_version || "",
    terms_url: consent.terms_url || item.terms_url || AI_CONSENT_POLICY_LINKS.termsOfUse,
    data_policy_url:
      consent.data_policy_url || item.data_policy_url || AI_CONSENT_POLICY_LINKS.aiDataUse,
    privacy_policy_url:
      consent.privacy_policy_url ||
      item.privacy_policy_url ||
      AI_CONSENT_POLICY_LINKS.privacyPolicy,
    requires_reacceptance: Boolean(
      consent.requires_reacceptance || item.requires_reacceptance
    )
  };
}

export function normalizeConsentUsersPage(response, page = 1, pageSize = 20) {
  const root = unwrapApiPayload(response) || {};
  const nested = root.pagination || {};
  const rawList =
    root.results ||
    root.list ||
    root.users ||
    (Array.isArray(root) ? root : []);
  const list = (Array.isArray(rawList) ? rawList : []).map(normalizeConsentUserRecord);
  const perPage = Number(root.page_size || nested.per_page || nested.page_size || pageSize) || pageSize;
  const count = Number(root.count ?? nested.total ?? nested.count ?? list.length) || 0;
  const totalPages = Math.max(
    1,
    Number(root.total_pages ?? nested.total_pages) || Math.ceil(count / perPage) || 1
  );

  return {
    list,
    pagination: {
      page: Number(root.page ?? nested.page ?? page) || page,
      per_page: perPage,
      total: count,
      total_pages: totalPages
    }
  };
}

export function normalizeConsentUserDetail(response) {
  const root = unwrapApiPayload(response) || {};
  const record =
    root.user ||
    root.consent ||
    root.current_consent ||
    root.acceptance ||
    root;
  return {
    ...normalizeConsentUserRecord(record),
    message: root.message,
    history_count: Number(root.history_count || root.acceptance_count || 0) || 0
  };
}

export function normalizeConsentHistoryPage(response, page = 1, pageSize = 20) {
  const root = unwrapApiPayload(response) || {};
  const nested = root.pagination || {};
  const rawList =
    root.results ||
    root.list ||
    root.history ||
    root.acceptances ||
    (Array.isArray(root) ? root : []);
  const list = (Array.isArray(rawList) ? rawList : []).map((item, index) => {
    const normalized = normalizeConsentUserRecord(item);
    return {
      ...normalized,
      id: item.id || `history-${index}`,
      user_name: item.user_name || "",
      accepted_at: item.accepted_at || item.created_at || normalized.accepted_at,
      acceptance_source: item.acceptance_source || "SPARTA_AI_CHAT_ASSISTANT"
    };
  });
  const perPage = Number(root.page_size || nested.per_page || nested.page_size || pageSize) || pageSize;
  const count = Number(root.count ?? nested.total ?? nested.count ?? list.length) || 0;
  const totalPages = Math.max(
    1,
    Number(root.total_pages ?? nested.total_pages) || Math.ceil(count / perPage) || 1
  );

  return {
    list,
    pagination: {
      page: Number(root.page ?? nested.page ?? page) || page,
      per_page: perPage,
      total: count,
      total_pages: totalPages
    }
  };
}
